import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PhoneInTalkOutlined,
  RequestQuoteOutlined,
} from '@mui/icons-material';
import { salesLeadService } from '../../services/salesLeadService';
import { salesActivityLogService } from '../../services/salesActivityLogService';
import { quoteRequestService } from '../../services/quoteRequestService';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  ACTIVITY_TYPE_META,
  LEAD_STATUS_META,
  QUOTE_REQUEST_STATUS_META,
  SalesCard,
  SalesEntityAvatar,
  SalesPageHeader,
  SalesSectionCard,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const EMPTY = {
  company_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
  email: '',
  website: '',
  business_info: '',
  contact_name: '',
  contact_position: '',
  contact_phone: '',
  contact_email: '',
  lead_source: '',
  marketing_materials_given: '',
  dropoff_items: '',
  questions_interests_specs: '',
  general_notes: '',
  status: 'new',
};

const ACTIVITY_TYPES = [
  { value: 'phone', label: 'Phone' },
  { value: 'onsite_visit', label: 'Onsite Visit' },
  { value: 'email', label: 'Email' },
  { value: 'other', label: 'Other' },
];

const SalesLeadDetail = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [tab, setTab] = useState(0);
  const [activities, setActivities] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({
    activity_type: 'phone',
    activity_date: new Date().toISOString().slice(0, 10),
    contact_name: '',
    notes: '',
    outcome: '',
    next_action: '',
    next_action_date: '',
    duration_minutes: '',
  });

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError('');
    try {
      const res = await salesLeadService.getById(id);
      setForm({ ...EMPTY, ...res.data });
      const [actRes, qrRes] = await Promise.all([
        salesActivityLogService.getAll({ lead_id: id, limit: 100 }),
        quoteRequestService.getAll({ lead_id: id, limit: 100 }),
      ]);
      setActivities(actRes.data || []);
      setQuoteRequests(qrRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (!form.company_name?.trim()) {
        setError('Company name is required');
        setSaving(false);
        return;
      }
      if (isNew) {
        const res = await salesLeadService.create(form);
        setMessage('Lead created');
        navigate(`/sales/leads/${res.data.id}`, { replace: true });
      } else {
        await salesLeadService.update(id, form);
        setMessage('Lead saved');
        await load();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateLog = async () => {
    if (!logForm.notes?.trim()) {
      setError('Activity notes are required');
      return;
    }
    try {
      await salesActivityLogService.create({
        ...logForm,
        lead_id: parseInt(id, 10),
        duration_minutes: logForm.duration_minutes
          ? parseInt(logForm.duration_minutes, 10)
          : null,
        next_action_date: logForm.next_action_date || null,
      });
      setLogOpen(false);
      setLogForm({
        activity_type: 'phone',
        activity_date: new Date().toISOString().slice(0, 10),
        contact_name: form.contact_name || '',
        notes: '',
        outcome: '',
        next_action: '',
        next_action_date: '',
        duration_minutes: '',
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to log activity');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <SalesPageHeader
        title={isNew ? 'New Lead' : (form.company_name || `Lead #${form.lead_number}`)}
        subtitle={
          isNew
            ? 'Digitize the paper Sales Leads form — company, contact, visit notes, and next steps.'
            : `Lead #${form.lead_number}${form.contact_name ? ` · ${form.contact_name}` : ''}`
        }
        actions={
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sales/leads')}
              sx={{ textTransform: 'none', color: SALES.muted }}
            >
              Back
            </Button>
            {!isNew && (
              <SalesStatusChip status={form.status} metaMap={LEAD_STATUS_META} />
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={salesPrimaryButtonSx}
            >
              {saving ? 'Saving…' : 'Save Lead'}
            </Button>
          </>
        }
      />

      {!isNew && (
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <SalesEntityAvatar name={form.company_name} size={44} />
          <Box>
            <Typography sx={{ fontWeight: 700, color: SALES.ink }}>{form.company_name}</Typography>
            <Typography variant="body2" sx={{ color: SALES.muted }}>
              {[form.city, form.state].filter(Boolean).join(', ') || 'Location not set'}
              {form.lead_source ? ` · Source: ${form.lead_source}` : ''}
            </Typography>
          </Box>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {!isNew && form.converted_customer_id && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Converted to customer #{form.converted_customer_number} — {form.converted_customer_name}
        </Alert>
      )}

      <SalesSectionCard title="Company & Address" subtitle="Who they are and where you visited">
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField fullWidth size="small" label="Company Name" required value={form.company_name} onChange={setField('company_name')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status}
                onChange={setField('status')}
                disabled={form.status === 'converted'}
              >
                {Object.entries(LEAD_STATUS_META)
                  .filter(([s]) => s !== 'converted' || form.status === 'converted')
                  .map(([s, meta]) => (
                    <MenuItem key={s} value={s}>{meta.label}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Address Line 1" value={form.address_line1 || ''} onChange={setField('address_line1')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Address Line 2" value={form.address_line2 || ''} onChange={setField('address_line2')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="City" value={form.city || ''} onChange={setField('city')} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth size="small" label="State" value={form.state || ''} onChange={setField('state')} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth size="small" label="Zip" value={form.zip_code || ''} onChange={setField('zip_code')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Phone" value={form.phone || ''} onChange={setField('phone')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Email" value={form.email || ''} onChange={setField('email')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Website" value={form.website || ''} onChange={setField('website')} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Business Info" value={form.business_info || ''} onChange={setField('business_info')} />
          </Grid>
        </Grid>
      </SalesSectionCard>

      <SalesSectionCard title="Point of Contact" subtitle="Primary person at this company">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Contact Name" value={form.contact_name || ''} onChange={setField('contact_name')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Position" value={form.contact_position || ''} onChange={setField('contact_position')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Contact Phone" value={form.contact_phone || ''} onChange={setField('contact_phone')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Contact Email" value={form.contact_email || ''} onChange={setField('contact_email')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Lead Source" value={form.lead_source || ''} onChange={setField('lead_source')} />
          </Grid>
        </Grid>
      </SalesSectionCard>

      <SalesSectionCard title="Visit & Opportunity Notes" subtitle="Materials left, specs, and general notes">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" multiline minRows={2} label="Marketing Materials Given" value={form.marketing_materials_given || ''} onChange={setField('marketing_materials_given')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" multiline minRows={2} label="Dropoff Items" value={form.dropoff_items || ''} onChange={setField('dropoff_items')} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={3} label="Questions / Interests / Specs" value={form.questions_interests_specs || ''} onChange={setField('questions_interests_specs')} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={3} label="General Notes" value={form.general_notes || ''} onChange={setField('general_notes')} />
          </Grid>
        </Grid>
      </SalesSectionCard>

      {!isNew && (
        <SalesCard>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 1,
              borderBottom: `1px solid ${SALES.line}`,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: SALES.steel },
              '& .MuiTabs-indicator': { bgcolor: SALES.steel },
            }}
          >
            <Tab label={`Activity (${activities.length})`} />
            <Tab label={`Quote Requests (${quoteRequests.length})`} />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {tab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PhoneInTalkOutlined />}
                    onClick={() => {
                      setLogForm((prev) => ({
                        ...prev,
                        contact_name: form.contact_name || '',
                        activity_date: new Date().toISOString().slice(0, 10),
                      }));
                      setLogOpen(true);
                    }}
                    sx={{ textTransform: 'none', borderColor: SALES.line }}
                  >
                    Log Call / Visit
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead sx={salesTableHeadSx}>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Next Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: SALES.muted }}>
                          No activity yet — log your first call or visit.
                        </TableCell>
                      </TableRow>
                    ) : activities.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{formatDateDisplay(a.activity_date)}</TableCell>
                        <TableCell>
                          <SalesStatusChip status={a.activity_type} metaMap={ACTIVITY_TYPE_META} />
                        </TableCell>
                        <TableCell>{a.contact_name || '—'}</TableCell>
                        <TableCell>{a.notes}</TableCell>
                        <TableCell>
                          {a.next_action || '—'}
                          {a.next_action_date ? ` (${formatDateDisplay(a.next_action_date)})` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {tab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RequestQuoteOutlined />}
                    onClick={() => navigate(`/sales/quote-requests/new?lead_id=${id}`)}
                    sx={{ textTransform: 'none', borderColor: SALES.line }}
                  >
                    New Quote Request
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead sx={salesTableHeadSx}>
                    <TableRow>
                      <TableCell>Request #</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quoteRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: SALES.muted }}>
                          No quote requests for this lead yet.
                        </TableCell>
                      </TableRow>
                    ) : quoteRequests.map((q) => (
                      <TableRow
                        key={q.id}
                        hover
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: SALES.steelSoft } }}
                        onClick={() => navigate(`/sales/quote-requests/${q.id}`)}
                      >
                        <TableCell sx={{ fontWeight: 700, color: SALES.steel }}>#{q.request_number}</TableCell>
                        <TableCell>{q.description || '—'}</TableCell>
                        <TableCell>
                          <SalesStatusChip status={q.status} metaMap={QUOTE_REQUEST_STATUS_META} />
                        </TableCell>
                        <TableCell>{q.submitted_at ? formatDateDisplay(q.submitted_at) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        </SalesCard>
      )}

      <Dialog open={logOpen} onClose={() => setLogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Log Activity</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={logForm.activity_type}
                  onChange={(e) => setLogForm({ ...logForm, activity_type: e.target.value })}
                >
                  {ACTIVITY_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
                value={logForm.activity_date}
                onChange={(e) => setLogForm({ ...logForm, activity_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Contact Name"
                value={logForm.contact_name}
                onChange={(e) => setLogForm({ ...logForm, contact_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Duration (minutes)"
                value={logForm.duration_minutes}
                onChange={(e) => setLogForm({ ...logForm, duration_minutes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" multiline minRows={3} label="Notes" required
                value={logForm.notes}
                onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Outcome"
                value={logForm.outcome}
                onChange={(e) => setLogForm({ ...logForm, outcome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Next Action"
                value={logForm.next_action}
                onChange={(e) => setLogForm({ ...logForm, next_action: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" type="date" label="Next Action Date" InputLabelProps={{ shrink: true }}
                value={logForm.next_action_date}
                onChange={(e) => setLogForm({ ...logForm, next_action_date: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLog} sx={salesPrimaryButtonSx}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesLeadDetail;
